/**
 * Review requests (owner 10-21, item 5): the executive's one question about a template and the admin's one
 * answer. Calls and types only; the Worker checks every rule (routes/reviews.ts).
 */
import type { ReviewState } from '@saledeed/shared';
import { api } from '../api/client';

export interface Review {
  id: string;
  projectId: string;
  projectName: string;
  templateCode: string;
  templateId: string;
  templateTitle: string;
  /** The version the executive's draft is on (pinned when the document was started). */
  templateVersion: number;
  /** The template's latest published version now. */
  currentVersion: number | null;
  documentId: string;
  /** The executive's document: a draft can be filled by the admin for him (owner 10-21 (6)). */
  documentState: 'draft' | 'completed' | null;
  createdBy: { userCode: string; displayName: string };
  message: string;
  state: ReviewState;
  /** The admin's answer, or the note given with Mark done. */
  reply: string | null;
  repliedBy: { displayName: string } | null;
  repliedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export type ReviewCounts = Record<string, { open: number; answered: number }>;

/** The executive's own requests in one project (react-query key `['my-reviews', projectId]`). */
export const myReviewsKey = (projectId: string) => ['my-reviews', projectId] as const;

export async function listReviews(query: { projectId?: string; templateCode?: string; state?: ReviewState | 'all' } = {}): Promise<Review[]> {
  const q = new URLSearchParams();
  if (query.projectId) q.set('projectId', query.projectId);
  if (query.templateCode) q.set('templateCode', query.templateCode);
  if (query.state) q.set('state', query.state);
  q.set('limit', '200');
  return (await api<{ reviews?: Review[] }>('GET', `/api/reviews?${q}`)).reviews ?? [];
}

export async function reviewCounts(): Promise<ReviewCounts> {
  return (await api<{ counts?: ReviewCounts }>('GET', '/api/reviews/counts')).counts ?? {};
}

export const askReview = (documentId: string, message: string) =>
  api<{ review: Review }>('POST', '/api/reviews', { body: { documentId, message } });

export const replyReview = (id: string, reply: string) =>
  api<{ review: Review }>('POST', `/api/reviews/${id}/reply`, { body: { reply } });

/** A blank note is not sent at all (the server reads a missing note as "no note"). */
export const markReviewDone = (id: string, note?: string) =>
  api<{ review: Review }>('POST', `/api/reviews/${id}/done`, { body: note ? { note } : {} });

/** Got it: the executive removes his request and its answer. */
export const deleteReview = (id: string) => api<{ ok: true }>('DELETE', `/api/reviews/${id}`);
